/**
 * Video-credit packs. A pack is a one-time Stripe price with the pack id as its lookup key. USD
 * and EUR cost the same number (the house rule for Music24 prices). Packs never expire. The
 * month's plan count goes first, then the oldest pack with credits left. Only the workspace owner
 * buys a pack, and only on a paid plan (Artist and up): Free and the trial render previews, so
 * they get the plan offer instead (pricing spec 2026-10-08 §5).
 *
 * The native apps sell the same packs as store consumables: the SKU is `storeSku` (the twin of
 * `CreditPackSku` in fym-common `utils/iapSkus.ts`; a test keeps them equal). The store price is
 * the web price (USD, EUR the same number); we absorb the store fee.
 *
 * `CREDIT_PACKS` are the packs on sale. The packs of the first pricing (5 / 15 / 50 credits) are
 * retired: nobody can buy them, but `creditPackById` and `creditPackByStoreSku` still find them, so
 * an old webhook, a refund or a late store purchase still grants and books its credits.
 *
 * The file has no imports, so every project can use it.
 */

/** The packs on sale since 2026-10-08. A checkout accepts only these. */
export const OFFERED_CREDIT_PACK_IDS = [
  'music24_creditpack_10',
  'music24_creditpack_30',
  'music24_creditpack_100',
] as const;
export type OfferedCreditPackId = (typeof OFFERED_CREDIT_PACK_IDS)[number];

/** The packs of the first pricing ($9 / $19 / $49). Retired; kept for old purchases and refunds. */
export const RETIRED_CREDIT_PACK_IDS = [
  'music24_creditpack_5',
  'music24_creditpack_15',
  'music24_creditpack_50',
] as const;

export const CREDIT_PACK_IDS = [...OFFERED_CREDIT_PACK_IDS, ...RETIRED_CREDIT_PACK_IDS] as const;
export type CreditPackId = (typeof CREDIT_PACK_IDS)[number];

export const CREDIT_PACK_STORE_SKUS = [
  'music24.credits.10',
  'music24.credits.30',
  'music24.credits.100',
  'music24.credits.5',
  'music24.credits.15',
  'music24.credits.50',
] as const;
export type CreditPackStoreSku = (typeof CREDIT_PACK_STORE_SKUS)[number];

export interface CreditPack {
  /** The Stripe price lookup key, too. */
  id: CreditPackId;
  credits: number;
  /** Whole dollars. */
  priceUsd: number;
  /** Whole euros. */
  priceEur: number;
  /** The App Store and Google Play product id of the consumable. */
  storeSku: CreditPackStoreSku;
  /** The store price in USD and in EUR (the same number). */
  storePrice: number;
  /** True for a pack that is no longer on sale. */
  retired: boolean;
}

/** A pack that is on sale. */
export type OfferedCreditPack = CreditPack & { id: OfferedCreditPackId };

/** The packs on sale, smallest first. */
export const CREDIT_PACKS: ReadonlyArray<OfferedCreditPack> = [
  {
    id: 'music24_creditpack_10',
    credits: 10,
    priceUsd: 15,
    priceEur: 15,
    storeSku: 'music24.credits.10',
    storePrice: 15,
    retired: false,
  },
  {
    id: 'music24_creditpack_30',
    credits: 30,
    priceUsd: 39,
    priceEur: 39,
    storeSku: 'music24.credits.30',
    storePrice: 39,
    retired: false,
  },
  {
    id: 'music24_creditpack_100',
    credits: 100,
    priceUsd: 119,
    priceEur: 119,
    storeSku: 'music24.credits.100',
    storePrice: 119,
    retired: false,
  },
];

/** The retired packs of the first pricing, with their old prices. Not on sale. */
export const RETIRED_CREDIT_PACKS: ReadonlyArray<CreditPack> = [
  {
    id: 'music24_creditpack_5',
    credits: 5,
    priceUsd: 9,
    priceEur: 9,
    storeSku: 'music24.credits.5',
    storePrice: 10.99,
    retired: true,
  },
  {
    id: 'music24_creditpack_15',
    credits: 15,
    priceUsd: 19,
    priceEur: 19,
    storeSku: 'music24.credits.15',
    storePrice: 22.99,
    retired: true,
  },
  {
    id: 'music24_creditpack_50',
    credits: 50,
    priceUsd: 49,
    priceEur: 49,
    storeSku: 'music24.credits.50',
    storePrice: 59.99,
    retired: true,
  },
];

/** Every pack that a purchase can name: the packs on sale and the retired ones. */
export const ALL_CREDIT_PACKS: ReadonlyArray<CreditPack> = [...CREDIT_PACKS, ...RETIRED_CREDIT_PACKS];

/** The pack that the buy dialog selects first. */
export const DEFAULT_CREDIT_PACK_ID: OfferedCreditPackId = 'music24_creditpack_30';

export function isCreditPackId(value: unknown): value is CreditPackId {
  return typeof value === 'string' && (CREDIT_PACK_IDS as readonly string[]).includes(value);
}

/** The pack of an id, retired packs included, or null. */
export function creditPackById(id: string): CreditPack | null {
  return ALL_CREDIT_PACKS.find((pack) => pack.id === id) ?? null;
}

/** The pack of a store product id, retired packs included, or null for a product that is not a pack. */
export function creditPackByStoreSku(productId: string): CreditPack | null {
  return ALL_CREDIT_PACKS.find((pack) => pack.storeSku === productId) ?? null;
}
